import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@klickdata/core/auth';
import { FormHelper } from '@klickdata/core/form';
import { Media } from '@klickdata/core/media/src/media.model';
import { RecorderService } from '@klickdata/core/media/src/recorder.service';
import { S3MediaService } from '@klickdata/core/media/src/s3-media.service';
import { MessageFormErrorComponent, MessageService } from '@klickdata/core/message';
import {
    AppScope,
    Resource,
    ResourceCategoryService,
    ResourceData,
    ResourceService,
    ResourceTypes,
} from '@klickdata/core/resource';
import { ResourceItem, ResourceItemData, ResourceItemService } from '@klickdata/core/resource-item';
import * as moment from 'moment';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, first, map, switchMap, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-upload-material-file',
    templateUrl: './upload-material-file.component.html',
    styleUrls: ['./upload-material-file.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadMaterialFileComponent implements OnInit, OnChanges, OnDestroy {
    public fileForm: FormGroup;
    public media: Media;
    AppScope = AppScope;
    private destroy: Subject<boolean> = new Subject<boolean>();
    public progress$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    @Output() saving: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() onError: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() saved: EventEmitter<{ res: Resource; isParentSubmission?: boolean }> = new EventEmitter<{
        res: Resource;
        isParentSubmission?: boolean;
    }>();
    @Input() resource: Resource;
    private resourceItem: ResourceItem;
    ResourceTypes = ResourceTypes;
    public canDownloadControl = new FormControl(true);
    @Input() publishOnly: boolean;
    @Input() active = true;
    @Input() publishLabel = $localize`Publish`;
    public isAdmin$: Observable<boolean>;
    private isParentSubmission: boolean;

    dropSize = '200px';
    public get dropStyle() {
        return {
            height: this.dropSize,
            width: this.dropSize,
            flex: `0 1 ${this.dropSize}`,
        };
    }
    constructor(
        protected fb: FormBuilder,
        protected resourceService: ResourceService,
        protected itemService: ResourceItemService,
        protected auth: AuthService,
        protected mediaService: S3MediaService,
        protected messageService: MessageService,
        protected changeRef: ChangeDetectorRef,
        protected route: ActivatedRoute,
        public router: Router,
        protected categoryService: ResourceCategoryService,
        protected recordingService: RecorderService
    ) {
        this.buildForm();
    }

    ngOnInit() {
        this.isAdmin$ = this.auth.getUser().pipe(
            first(),
            map((user) => user.isAdmin())
        );
        this.setDefaultCategory();
        this.route.queryParams
            .pipe(
                takeUntil(this.destroy),
                filter((params) => params.naviagteAfterRecord),
                switchMap(() => this.recordingService.recordedSub.asObservable())
            )
            .subscribe((data) => {
                if (data && data.file) {
                    this.fileHandler(data.file);
                }
            });
    }

    private setDefaultCategory() {
        this.categoryService
            .getSuggestedCategory(ResourceTypes.MATERIAL)
            .pipe(takeUntil(this.destroy))
            .subscribe((category) => {
                if (!this.fileForm.controls.category_ids.value?.length) {
                    this.fileForm.patchValue({
                        category_ids: [category.id],
                    });
                    FormHelper.resetForm(<FormControl>this.fileForm.controls.category_ids);
                }
            });
    }

    ngOnChanges() {
        if (this.resource) {
            this.fileForm.patchValue({
                id: this.resource.id,
                priority: this.resource.priority,
                title: this.resource.title,
                media_id: this.resource.media_id,
                description: this.resource.description,
                instructions: this.resource.instructions,
                category_ids: this.resource.category_ids,
                language_id: this.resource.language_id,
            });
            FormHelper.resetForm(this.fileForm);
            this.update();
        }
    }

    public buildForm() {
        this.fileForm = this.fb.group({
            id: [],
            priority: [],
            media_id: [],
            title: ['', Validators.required],
            file: ['', Validators.required],
            tag_ids: [[]],
            category_ids: [[], Validators.required],
            description: [''],
            instructions: [''],
            language_id: ['', Validators.required],
        });
    }

    public update(): void {
        this.resource.item$?.pipe(takeUntil(this.destroy)).subscribe((item) => {
            this.resourceItem = item;
            this.fileForm.patchValue({
                file: item.name,
            });
            this.canDownloadControl.patchValue(item.can_download);
            FormHelper.resetForm(this.fileForm);
            this.changeRef.markForCheck();
        });
    }
    public submitMaterial() {
        this.isParentSubmission = true;
        this.submitFile();
    }
    public submitFile(form?: FormGroupDirective, publish?: boolean) {
        if (!this.fileForm.valid) {
            FormHelper.markForm(this.fileForm);
            this.messageService.openMessage(MessageFormErrorComponent);
            this.onError.emit(true);
            return;
        }

        this.prepareSubmit(publish).subscribe((resource) => {
            form?.resetForm();
            this.fileForm.reset();
            FormHelper.resetForm(this.fileForm);
            this.saving.emit(false);
            this.saved.emit({ res: resource, isParentSubmission: this.isParentSubmission });
            this.isParentSubmission = false;
            if (this.publishOnly) {
                this.setDefaultCategory();
            }
        });
    }

    public prepareSubmit(publish: boolean): Observable<Resource> {
        this.saving.emit(true);
        return this.prepareData(publish).pipe(
            switchMap((data) =>
                !this.resource?.id ? this.resourceService.store(data) : this.resourceService.update(data)
            ),
            takeUntil(this.destroy)
        );
    }

    ngOnDestroy() {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }
    protected prepareMaterialFileItem(): ResourceItemData[] {
        if (this.fileForm.value.file !== '') {
            return this.resourceItem && this.resourceItem.id
                ? [
                      {
                          id: this.resourceItem.id,
                          name: this.fileForm.value.file,
                          can_download: this.canDownloadControl.value,
                      },
                  ]
                : [
                      {
                          name: this.fileForm.value.file,
                          item_type_value: this.media ? this.media.type : 'doc_material',
                          can_download: this.canDownloadControl.value,
                      },
                  ];
        } else {
            if (this.resource && this.resourceItem?.id) {
                return [{ id: this.resourceItem.id, deleted: true }];
            }
        }
    }
    protected prepareData(publish: boolean): Observable<ResourceData> {
        const data = new Resource(this.fileForm.value).getData();
        data.resource_items = this.prepareMaterialFileItem();
        if (publish) {
            data.published = moment().format('YYYY-MM-DD HH:mm:ss');
        }
        if (this.media && this.media.path) {
            data.article_code = this.media.path;
        }

        if (!this.resource?.id) {
            return combineLatest([this.auth.getCustomer(), this.auth.getUser()]).pipe(
                first(),
                map(([customer, user]) => {
                    data.author_id = user.id;
                    data.customer_id = customer.id;
                    data.type_id = ResourceTypes.DocMaterial;
                    return data;
                })
            );
        } else {
            return of(data);
        }
    }

    public canDeactivate(): boolean {
        return this.fileForm.pristine;
    }

    public prepareFile(event) {
        const fileList: FileList = event.target.files;
        if (fileList.length > 0) {
            this.fileHandler(fileList[0]);
        }
    }

    public fileHandler(file: File) {
        this.saving.emit(true);
        this.auth
            .getCustomer()
            .pipe(
                first(),
                takeUntil(this.destroy),
                switchMap((customer) => this.mediaService.uploadMaterialToS3(customer.id, file, this.progress$))
            )
            .subscribe(
                (mediaKey) => {
                    this.saving.emit(false);
                    if (mediaKey) {
                        this.fileForm.patchValue({
                            file: mediaKey.src,
                            title: this.fileForm.value.title || mediaKey.filename,
                        });
                        this.media = mediaKey;
                        FormHelper.markForm(this.fileForm);
                    }
                },
                (err) => {} // TODO on error
            );
    }
}
